import { decryptField, encryptField } from '../crypto/aesgcm';
import { hmacToken } from '../crypto/hmac';
import { randomBytes } from '../crypto/encoding';
import { bytesToBase64Url } from '../crypto/encoding';
import type { CruzFindVault, TaxpayerRow, TransactionRow } from './schema';

export interface TaxpayerPlain {
  token: string;
  name: string;
  address: string;
  tin: string;
  createdAt: number;
}

export interface TaxpayerInput {
  name: string;
  address: string;
  tin: string;
}

export interface TransactionPlain {
  id: string;
  taxpayerToken: string;
  amount: string;
  description: string;
  createdAt: number;
}

export interface TransactionInput {
  taxpayerToken: string;
  amount: string;
  description: string;
}

function newTransactionId(): string {
  // 16 random bytes → 22 base64url chars. Adequate for client-local IDs.
  return bytesToBase64Url(randomBytes(16));
}

export class TaxpayerRepo {
  constructor(
    private readonly db: CruzFindVault,
    private readonly aesKey: CryptoKey,
    private readonly hmacKey: CryptoKey,
  ) {}

  async add(input: TaxpayerInput, now: number = Date.now()): Promise<TaxpayerPlain> {
    const token = await hmacToken(this.hmacKey, input.tin);
    const [encryptedName, encryptedAddress, encryptedTin] = await Promise.all([
      encryptField(this.aesKey, input.name),
      encryptField(this.aesKey, input.address),
      encryptField(this.aesKey, input.tin),
    ]);
    const row: TaxpayerRow = {
      token,
      encryptedName,
      encryptedAddress,
      encryptedTin,
      createdAt: now,
    };
    await this.db.taxpayers.put(row);
    return { token, name: input.name, address: input.address, tin: input.tin, createdAt: now };
  }

  async getByToken(token: string): Promise<TaxpayerPlain | null> {
    const row = await this.db.taxpayers.get(token);
    if (!row) return null;
    return this.decryptRow(row);
  }

  async list(): Promise<TaxpayerPlain[]> {
    const rows = await this.db.taxpayers.orderBy('createdAt').toArray();
    return Promise.all(rows.map((r) => this.decryptRow(r)));
  }

  private async decryptRow(row: TaxpayerRow): Promise<TaxpayerPlain> {
    const [name, address, tin] = await Promise.all([
      decryptField(this.aesKey, row.encryptedName),
      decryptField(this.aesKey, row.encryptedAddress),
      decryptField(this.aesKey, row.encryptedTin),
    ]);
    return { token: row.token, name, address, tin, createdAt: row.createdAt };
  }
}

export class TransactionRepo {
  constructor(
    private readonly db: CruzFindVault,
    private readonly aesKey: CryptoKey,
  ) {}

  async add(input: TransactionInput, now: number = Date.now()): Promise<TransactionPlain> {
    const id = newTransactionId();
    const [encryptedAmount, encryptedDescription] = await Promise.all([
      encryptField(this.aesKey, input.amount),
      encryptField(this.aesKey, input.description),
    ]);
    const row: TransactionRow = {
      id,
      taxpayerToken: input.taxpayerToken,
      encryptedAmount,
      encryptedDescription,
      createdAt: now,
    };
    await this.db.transactions.put(row);
    return {
      id,
      taxpayerToken: input.taxpayerToken,
      amount: input.amount,
      description: input.description,
      createdAt: now,
    };
  }

  async listForTaxpayer(taxpayerToken: string): Promise<TransactionPlain[]> {
    const rows = await this.db.transactions
      .where('taxpayerToken')
      .equals(taxpayerToken)
      .sortBy('createdAt');
    return Promise.all(
      rows.map(async (row) => {
        const [amount, description] = await Promise.all([
          decryptField(this.aesKey, row.encryptedAmount),
          decryptField(this.aesKey, row.encryptedDescription),
        ]);
        return {
          id: row.id,
          taxpayerToken: row.taxpayerToken,
          amount,
          description,
          createdAt: row.createdAt,
        };
      }),
    );
  }
}
